
import { read, utils } from 'xlsx';
import { CycleImportRow, ValidationResult } from './types';
import { ValidationError } from '../types';
import { validateHeaderRow, validateDataRow } from './validation';

/**
 * Parses an Excel or CSV file containing cycle data
 */
export const parseExcelFile = async (file: File): Promise<ValidationResult> => {
  const data: CycleImportRow[] = [];
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result;
        const workbook = read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
        
        // Validate header row
        const headerRow = jsonData[0] as string[];
        const headerErrors = validateHeaderRow(headerRow);
        errors.push(...headerErrors);
        
        // If header validation fails, return early
        if (headerErrors.length > 0) {
          resolve({ data, errors, warnings });
          return;
        }
        
        // Process data rows
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          
          if (!row || row.length === 0) continue;
          
          const { rowData, errors: rowErrors, warnings: rowWarnings } = validateDataRow(row, i);
          
          errors.push(...rowErrors);
          warnings.push(...rowWarnings);
          
          // Only add if required fields are present
          if (rowData.cycleStartDate && rowData.cycleEndDate) {
            data.push(rowData as CycleImportRow);
          }
        }
        
        resolve({ data, errors, warnings });
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        errors.push({
          rowIndex: 0,
          message: 'Failed to parse file. Please ensure it is a valid Excel or CSV file.',
          field: 'file',
          type: 'error'
        });
        resolve({ data, errors, warnings });
      }
    };
    
    reader.onerror = () => {
      errors.push({
        rowIndex: 0,
        message: 'Failed to read file.',
        field: 'file',
        type: 'error'
      });
      resolve({ data, errors, warnings });
    };
    
    reader.readAsArrayBuffer(file);
  });
};
